import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Play, ArrowRight, ArrowLeft, Box } from 'lucide-react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';

const EquiposPorCategoria = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [equipos, setEquipos] = useState([]);
  const [categoriaNombre, setCategoriaNombre] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEquipos = async () => {
      setLoading(true);
      try {
        const response = await apiService.getEquiposPorCategoria(id, 24); // Aumentado a 24 para grillas de 3 o 4 columnas
        if (response.estado === 1 && response.data.length > 0) {
          setEquipos(response.data);
          setCategoriaNombre(response.data[0].categoria_nombre);
        } else {
          setEquipos([]);
        }
      } catch (error) {
        console.error('Error cargando equipos:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchEquipos();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center pt-20">
        <motion.div 
          className="w-16 h-16 border-4 border-yellow-600/30 border-t-yellow-500 rounded-full" 
          animate={{ rotate: 360 }} 
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }} 
        />
        <p className="text-yellow-500/70 mt-4 text-sm tracking-widest uppercase">Cargando equipos...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pt-28 pb-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* --- ENCABEZADO CENTRADO Y CORPORATIVO --- */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <button 
            onClick={() => navigate(-1)} 
            className="inline-flex items-center gap-2 text-gray-500 hover:text-yellow-500 transition-colors mb-8 group text-sm font-medium tracking-wide uppercase"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-300" />
            <span>Volver al menú</span>
          </button>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
            Equipos de <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600">{categoriaNombre || 'Categoría'}</span>
          </h1>
          
          {/* Línea decorativa dorada */}
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-yellow-500 to-transparent mx-auto mb-6 rounded-full" />
          
          <p className="text-gray-400 text-lg md:text-xl leading-relaxed">
            Explora nuestra selección profesional de alta gama para garantizar 
            el máximo rendimiento en tu próximo evento.
          </p>
        </motion.div>

        {/* --- ESTADO VACÍO --- */}
        {equipos.length === 0 ? (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }} 
            animate={{ opacity: 1, scale: 1 }} 
            className="text-center py-24 bg-neutral-900/30 rounded-3xl border border-yellow-600/20 max-w-2xl mx-auto"
          >
            <Box className="w-16 h-16 text-yellow-600/40 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2">Categoría sin equipos</h3>
            <p className="text-gray-400 mb-6">No hay equipos disponibles en esta sección por el momento.</p>
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-yellow-500 hover:text-yellow-400 font-semibold transition-colors"
            >
              Volver al inicio <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        ) : (
          /* --- GRILLA DE EQUIPOS (RESPONSIVE Y ORDENADA) --- */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
            {equipos.map((equipo, index) => (
              <motion.div
                key={equipo.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: index * 0.05, duration: 0.5 }}
              >
                <Link to={`/equipo/${equipo.id}`} className="group block h-full">
                  <div className="relative h-full bg-neutral-900/40 border border-yellow-600/20 rounded-2xl overflow-hidden hover:border-yellow-500/50 transition-all duration-500 hover:shadow-[0_10px_40px_-15px_rgba(234,179,8,0.2)] flex flex-col">
                    
                    {/* Media (Video o Imagen) - Proporción fija para uniformidad */}
                    <div className="relative aspect-[4/3] overflow-hidden bg-black">
                      {equipo.imagen_principal_es_video ? (
                        <video 
                          src={equipo.imagen_principal_url} 
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          autoPlay muted loop playsInline
                        />
                      ) : (
                        <img 
                          src={equipo.imagen_principal_url} 
                          alt={equipo.nombre} 
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          loading="lazy"
                        />
                      )}
                      
                      {/* Overlay gradiente siempre presente para legibilidad */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-500" />
                      
                      {/* Badge de Video */}
                      {equipo.imagen_principal_es_video && (
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:scale-100 scale-90">
                          <div className="w-14 h-14 bg-yellow-500/90 backdrop-blur-md rounded-full flex items-center justify-center shadow-lg shadow-yellow-500/20">
                            <Play className="w-6 h-6 text-black ml-1" fill="black" />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Contenido de la tarjeta */}
                    <div className="p-5 flex flex-col flex-grow">
                      <h3 className="text-lg font-bold text-white mb-2 group-hover:text-yellow-500 transition-colors duration-300 line-clamp-2 min-h-[3.5rem]">
                        {equipo.nombre}
                      </h3>
                      
                      <p className="text-gray-400 text-sm leading-relaxed mb-4 line-clamp-3 flex-grow">
                        {equipo.descripcion_corta}
                      </p>
                      
                      {/* Separador y botón de acción (mt-auto asegura que siempre esté abajo) */}
                      <div className="mt-auto pt-4 border-t border-yellow-600/20 flex items-center text-yellow-500 font-semibold text-sm group-hover:translate-x-1 transition-transform duration-300">
                        <span>Ver especificaciones</span>
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EquiposPorCategoria;